import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    CalendarDays,
    Download,
    Eye,
    FileText,
    Hash,
    Link2,
    Pencil,
    StickyNote,
    Tag,
    Clock,
    type LucideIcon,
} from 'lucide-react';
import { useDocument, useDeleteDocument, useUpdateDocumentStatus } from '@/lib/api/hooks/document';
import { ActionDropdown, Button, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import handleDownloadFromUrl from '@/utils/helpers/handleDownloadFromUrl';

const DocumentDetailPage: React.FC = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: documentData, isLoading, error } = useDocument(documentId);
    const document = documentData?.data;

    const { mutate: deleteDocument, isPending: isDeleting } = useDeleteDocument();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateDocumentStatus();

    if (!documentId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Document ID missing</h2>
                <button onClick={() => navigate('/admin/documents')} className="mt-4 text-forestGreen-100 hover:underline">
                    &larr; Back to Procurement Docs
                </button>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading document...</p>
                </div>
            </div>
        );
    }

    if (error || !document) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading document</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Document not found'}</p>
                <button onClick={() => navigate('/admin/documents')} className="mt-4 text-forestGreen-100 hover:underline">
                    &larr; Back to Procurement Docs
                </button>
            </div>
        );
    }

    const uploaderName = (() => {
        const name = document.publishedByName || document.createdByName || document.username || 'Admin';
        return (!name || name === 'N/A') ? 'Admin' : name;
    })();
    const createdAt = document.createdAt || document.datePublished;
    const updatedAt = document.updatedAt || document.datePublished;
    const totalDownloads = 0;
    const totalViews = 0;
    const description: string | null = null;
    const tags: string[] = [];
    const notes: string | null = null;

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return dateString; }
    };

    const formatCategory = (category: string) =>
        category.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

    const getInitials = (name: string) => {
        const parts = name.trim().split(' ').filter(Boolean);
        if (!parts.length) return 'A';
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    const getFileType = (url?: string) => {
        if (!url) return 'FILE';
        const clean = url.split('?')[0].split('#')[0];
        const ext = clean.includes('.') ? clean.substring(clean.lastIndexOf('.') + 1).toUpperCase() : '';
        return ext || 'FILE';
    };

    const fileType = getFileType(document.documentUrl);

    const fileTypeConfig: Record<string, { bg: string; text: string; border: string }> = {
        PDF:  { bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200'     },
        DOCX: { bg: 'bg-blue-50',    text: 'text-blue-600',    border: 'border-blue-200'    },
        DOC:  { bg: 'bg-blue-50',    text: 'text-blue-600',    border: 'border-blue-200'    },
        PPTX: { bg: 'bg-orange-50',  text: 'text-orange-600',  border: 'border-orange-200'  },
        PPT:  { bg: 'bg-orange-50',  text: 'text-orange-600',  border: 'border-orange-200'  },
        XLSX: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
        XLS:  { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
        CSV:  { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    };
    const ftConfig = fileTypeConfig[fileType] ?? { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200' };

    const handleEdit = () => navigate(`/admin/documents/edit/${documentId}`, { state: { document } });

    const handleDownload = async () => {
        if (!document.documentUrl) {
            showToast.error('No document URL available for download.');
            return;
        }
        handleDownloadFromUrl({ url: document.documentUrl, fileName: document.title || 'document' });
    };

    const confirmDelete = () => {
        deleteDocument(
            { documentId },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success('Document deleted successfully!');
                        setDeleteModalOpen(false);
                        navigate('/admin/documents');
                    }
                },
                onError: (err: Error) => showToast.error('Failed to delete: ' + (err.message || 'Unknown error')),
            }
        );
    };

    const confirmStatusUpdate = () => {
        const newStatus = document.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
        updateStatus(
            { documentId, data: { status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(newStatus === 'PUBLISH' ? 'Document published successfully!' : 'Document unpublished successfully!');
                        setStatusModalOpen(false);
                    }
                },
                onError: (err: Error) => showToast.error('Failed to update status: ' + (err.message || 'Unknown error')),
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: document.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
                onClick: (e) => { e.stopPropagation(); setStatusModalOpen(true); },
                disabled: isUpdatingStatus,
            },
        ];
        if (document.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => { e.stopPropagation(); setDeleteModalOpen(true); },
                variant: 'danger' as const,
                disabled: isDeleting,
            });
        }
        return actions;
    };

    const isPublished = document.status === 'PUBLISH';

    const statsItems: Array<{ label: string; value: string | number; sub: string; icon: LucideIcon; iconClassName: string }> = [
        { label: 'Downloads',    value: totalDownloads.toLocaleString(), sub: 'Total downloads',  icon: Download,     iconClassName: 'bg-emerald-50 text-emerald-600'  },
        { label: 'Views',        value: totalViews.toLocaleString(),     sub: 'Total views',      icon: Eye,          iconClassName: 'bg-emerald-50 text-emerald-600'  },
        { label: 'Version',      value: '1.0',                           sub: 'Current version',  icon: FileText,     iconClassName: 'bg-emerald-50 text-emerald-600'  },
        { label: 'Last Updated', value: formatDate(updatedAt),           sub: `by ${uploaderName}`, icon: Clock,      iconClassName: 'bg-emerald-50 text-emerald-600'  },
    ];

    const detailRows: Array<{ icon: LucideIcon; label: string; content: React.ReactNode }> = [
        {
            icon: CalendarDays,
            label: 'Created',
            content: <span className="text-gray-900 font-medium">{formatDate(createdAt)}</span>,
        },
        {
            icon: Tag,
            label: 'Category',
            content: (
                <span className="inline-flex items-center rounded-md bg-rose-50 px-3 py-0.5 text-sm font-medium text-rose-700 border border-rose-200">
                    {formatCategory(document.documentCategory)}
                </span>
            ),
        },
        {
            icon: Tag,
            label: 'Tags',
            content: (
                <div className="flex flex-wrap items-center gap-2">
                    {tags.length > 0 ? tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded-md bg-emerald-50 px-3 py-0.5 text-sm font-medium text-emerald-700 border border-emerald-200">
                            {tag}
                        </span>
                    )) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                    )}
                </div>
            ),
        },
        {
            icon: StickyNote,
            label: 'Notes',
            content: <span className="text-gray-500 text-sm">{notes || 'No notes available'}</span>,
        },
        {
            icon: Hash,
            label: 'Document ID',
            content: <span className="text-gray-900 font-medium text-sm font-mono">{document.documentId}</span>,
        },
        {
            icon: Link2,
            label: 'Permalink',
            content: document.documentUrl ? (
                <a
                    href={document.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forestGreen-100 hover:underline text-sm truncate max-w-[240px] inline-block"
                >
                    {document.documentUrl}
                </a>
            ) : (
                <span className="text-gray-400 text-sm">—</span>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-6">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/admin/documents" className="text-forestGreen-100 hover:underline font-medium">
                        Procurement Docs
                    </Link>
                    <span className="text-gray-400">▸</span>
                    <span className="text-gray-600">{document.title}</span>
                </div>

                {/* ── Header Card ── */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    {/* Top row: category badge + action buttons */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                        <span className="inline-flex items-center rounded-md bg-rose-50 px-3 py-1 text-sm font-medium text-rose-700 border border-rose-200">
                            {formatCategory(document.documentCategory)}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                type="button"
                                onClick={handleEdit}
                                variant='secondary'
                                className="inline-flex items-center gap-2 border border-green-300 px-4 py-2 text-sm font-medium text-green-700 hover:bg-gray-50"
                            >
                                <Pencil className="w-4 h-4" />
                                Edit Document
                            </Button>
                            <Button
                                type="button"
                                onClick={handleDownload}
                                    variant='secondary'
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <Download className="w-4 h-4" />
                                Download
                            </Button>
                            <ActionDropdown
                                id={`document-${documentId}`}
                                actions={getDropdownActions()}
                            />
                        </div>
                    </div>

                    {/* Title + publish info */}
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{document.title}</h1>
                    <div className="flex items-center gap-3 mb-5">
                        <CalendarDays className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                            Published: {formatDate(document.datePublished)}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs font-semibold ${isPublished ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                            {isPublished ? 'Published' : 'Draft'}
                        </span>
                    </div>

                    {/* Meta row: Uploaded by | File type */}
                    <div className="flex items-center flex-wrap gap-0 border-t border-gray-200 pt-4">
                        <div className="flex items-center gap-3 pr-8 border-r border-gray-200">
                            <span className="text-gray-400">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </span>
                            <div className='flex flex-col gap-1'>
                                <p className="text-sm text-gray-400 mb-0.5">Uploaded by</p>
                                <div className="flex items-center gap-2">
                                    <div className="h-7 w-7 rounded-full bg-gray-200 text-gray-700 text-sm font-bold flex items-center justify-center shrink-0">
                                        {getInitials(uploaderName)}
                                    </div>
                                    <div className='flex flex-col gap-1'>
                                        <p className="text-sm font-semibold text-gray-900 leading-tight">{uploaderName}</p>
                                        <p className="text-sm text-gray-500">Admin</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 px-8 border-r border-gray-200">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div className='flex flex-col gap-1'>
                                <p className="text-sm text-gray-400 mb-0.5">File type</p>
                                <p className="text-sm font-semibold text-gray-900">{fileType}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Stats Row ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {statsItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <div key={item.label} className="bg-white border border-gray-200 rounded-xl p-4 2xl:gap-6 2xl:p-6 shadow-sm grid grid-cols-[auto_1fr] gap-3">
                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${item.iconClassName}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className='flex flex-col gap-1'>
                                       <p className="text-sm text-gray-500">{item.label}</p>
                                    <p className={`leading-[1.1] font-semibold text-gray-900 ${item.label.toLowerCase() === 'last updated' ? 'text-[1.25rem]' : 'text-[2rem]'}`}>{item.value}</p>
                                <p className="text-sm text-gray-400">{item.sub}</p>
                                </div>
                             
                            </div>
                        );
                    })}
                </div>

                {/* ── Bottom two-column ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Document File */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h2 className="text-base font-semibold text-gray-900 mb-4">Document File</h2>
                        <div className="flex items-start gap-4">
                            {/* File thumbnail */}
                            <div className={`h-24 w-20 rounded-lg border ${ftConfig.border} ${ftConfig.bg} flex flex-col items-center justify-center shrink-0 gap-1`}>
                                <FileText className={`w-8 h-8 ${ftConfig.text}`} />
                                <span className={`text-[10px] font-bold ${ftConfig.text}`}>{fileType}</span>
                            </div>
                            {/* File info + actions */}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 break-words">{document.title}.{fileType.toLowerCase()}</p>
                                <p className="text-sm text-gray-500 mb-4">{fileType} Document</p>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={document.documentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 rounded-lg bg-forestGreen-100 px-4 py-2 text-sm font-semibold text-white hover:bg-forestGreen-100/90"
                                    >
                                        <FileText className="w-4 h-4" />
                                        View Document
                                    </a>
                                    <button
                                        type="button"
                                        onClick={handleDownload}
                                        className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Document Details */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h2 className="text-base font-semibold text-gray-900 mb-4">Document Details</h2>
                        <div className="divide-y divide-gray-100">
                            {detailRows.map((row) => {
                                const RowIcon = row.icon;
                                return (
                                    <div key={row.label} className="flex items-start justify-between gap-4 py-2">
                                        <span className="flex items-center gap-2 text-sm text-gray-500 shrink-0 min-w-[100px]">
                                            <RowIcon className="w-4 h-4 shrink-0" />
                                            {row.label}
                                        </span>
                                        <div className="text-right flex-1 min-w-0">{row.content}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ── Document Description ── */}
                {description && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <h2 className="text-base font-semibold text-gray-900 mb-3">Document Description</h2>
                        <div
                            className="text-sm text-gray-700 leading-relaxed prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Document"
                subtitle="Are you sure you want to delete this document?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Document"
            />
            {document.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Document"
                    subtitle="Are you sure you want to publish this document?"
                    itemType="Document"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Document"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default DocumentDetailPage;
